import { useState } from 'react';

export default function TimestampConverter() {
    const [timestamp, setTimestamp] = useState('');
    const [datetime, setDatetime] = useState('');
    const [currentTime, setCurrentTime] = useState(Date.now());

    // Update current time every second
    useState(() => {
        const interval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);
        return () => clearInterval(interval);
    });

    const timestampToDate = () => {
        try {
            const ts = parseInt(timestamp);
            if (isNaN(ts)) {
                setDatetime('Invalid timestamp');
                return;
            }
            // Auto-detect if timestamp is in seconds or milliseconds
            const date = ts.toString().length <= 10
                ? new Date(ts * 1000)
                : new Date(ts);

            if (isNaN(date.getTime())) {
                setDatetime('Invalid timestamp');
                return;
            }

            const formatted = {
                iso: date.toISOString(),
                local: date.toLocaleString(),
                utc: date.toUTCString(),
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
            };

            setDatetime(JSON.stringify(formatted, null, 2));
        } catch (e) {
            setDatetime('Error converting timestamp');
        }
    };

    const dateToTimestamp = () => {
        try {
            const date = new Date(datetime);
            if (isNaN(date.getTime())) {
                setTimestamp('Invalid date');
                return;
            }
            setTimestamp(date.getTime().toString());
        } catch (e) {
            setTimestamp('Error converting date');
        }
    };

    const useCurrentTime = () => {
        const now = Date.now();
        setTimestamp(now.toString());
        timestampToDate();
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            alert('Copied to clipboard!');
        } catch (e) {
            alert('Failed to copy');
        }
    };

    const currentDate = new Date(currentTime);

    return (
        <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-2">Timestamp Converter</h1>
            <p className="text-gray-600 mb-6">Convert between Unix timestamps and human-readable dates</p>

            {/* Current Time Display */}
            <div className="tool-card mb-6 bg-primary-50 border-primary-200">
                <h3 className="font-semibold mb-3">Current Time</h3>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-600 mb-1">Unix Timestamp (ms)</p>
                        <div className="flex items-center gap-2">
                            <code className="text-lg font-mono font-semibold">{currentTime}</code>
                            <button
                                onClick={() => copyToClipboard(currentTime.toString())}
                                className="text-primary-600 hover:text-primary-700 text-sm"
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600 mb-1">Unix Timestamp (s)</p>
                        <div className="flex items-center gap-2">
                            <code className="text-lg font-mono font-semibold">{Math.floor(currentTime / 1000)}</code>
                            <button
                                onClick={() => copyToClipboard(Math.floor(currentTime / 1000).toString())}
                                className="text-primary-600 hover:text-primary-700 text-sm"
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                </div>
                <p className="text-gray-700 mt-3">{currentDate.toLocaleString()}</p>
                <p className="text-gray-600 text-sm mt-1">{currentDate.toISOString()}</p>
            </div>

            {/* Timestamp to Date */}
            <div className="tool-card mb-6">
                <h3 className="font-semibold mb-3">Timestamp → Date</h3>
                <div className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={timestamp}
                        onChange={(e) => setTimestamp(e.target.value)}
                        placeholder="Enter Unix timestamp (seconds or milliseconds)"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-mono"
                    />
                    <button onClick={useCurrentTime} className="btn-secondary whitespace-nowrap">
                        Use Now
                    </button>
                    <button onClick={timestampToDate} className="btn-primary">
                        Convert
                    </button>
                </div>
                {datetime && (
                    <div>
                        <pre className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm overflow-auto">
                            {datetime}
                        </pre>
                    </div>
                )}
            </div>

            {/* Date to Timestamp */}
            <div className="tool-card">
                <h3 className="font-semibold mb-3">Date → Timestamp</h3>
                <div className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={datetime}
                        onChange={(e) => setDatetime(e.target.value)}
                        placeholder="Enter date (e.g., 2024-01-01 or Jan 1, 2024)"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <button onClick={dateToTimestamp} className="btn-primary">
                        Convert
                    </button>
                </div>
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2">💡 About Unix Timestamps</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Unix timestamp = seconds since Jan 1, 1970 00:00:00 UTC (epoch)</li>
                    <li>• 10 digits = seconds (e.g., 1609459200)</li>
                    <li>• 13 digits = milliseconds (e.g., 1609459200000)</li>
                    <li>• Supported date formats: ISO 8601, RFC 2822, or natural language</li>
                </ul>
            </div>
        </div>
    );
}
